import React, { useEffect, useMemo, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { Button, IconButton, Popover, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { SelectAutocomplete, SelectDateRange } from "components/forms";
import { RoleComponentGuard } from "components/guards";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DEAL_INDEX, indexNames } from "constants/reports";
import { useActionHook, useFormatNumericHook } from "hooks";
import { formatCubicValue } from "utils";
import {
  useDealIdSelector,
  useDealSubAssetsSelector,
  useDealTitleSelector,
  useReportsActions,
  useReportsMetaSelector,
  useReportsQueriesSelector
} from "store";
import { CubicData, CubicPageType, DateRangeValue } from "store/types";

import { RoleName } from "../../constants";

interface Props {
  dateRange: DateRangeValue<string>;
  setDateRange: (dateRange: DateRangeValue<string>) => void;
}

export const KPIs: React.FC<Props> = ({ dateRange, setDateRange }) => {
  const [menuState, setMenuState] = useState<MenuProps | null>(null);

  const { getCubicAction, setQueriesAction } = useReportsActions();
  const formatNumeric = useFormatNumericHook();

  const queries = useReportsQueriesSelector();
  const { cubicDealDashboard, exchangeMethod, currency, use_accounting_date } = queries;
  const subAssets = useDealSubAssetsSelector();
  const dealTitle = useDealTitleSelector();
  const dealId = useDealIdSelector();

  const { data: cubicData, action: getCubic, loading: getCubicsLoading } = useActionHook(getCubicAction);

  const handleApply = ({
    key,
    ...data
  }: {
    key: string;
    title: string;
    filter: string;
    index?: { index: string; name: string };
  }) => {
    setQueriesAction({ cubicDealDashboard: { ...queries["cubicDealDashboard"], [key]: data } });
    setMenuState(null);
  };

  useEffect(() => {
    if (Object.keys(cubicDealDashboard).length && dealTitle) {
      const subAssetNames = subAssets.map((asset) => asset.title);
      getCubic({
        cubics: cubicDealDashboard,
        pageType: "cubicDealDashboard",
        // filters: { ["deal_name"]: showChildren ? [dealTitle, ...subAssetNames] : [dealTitle] },
        filters: { ["deal_name"]: [dealTitle, ...subAssetNames] },
        exchangeMethod,
        currency,
        dateRange,
        use_accounting_date
      });
    }
  }, [dealId, cubicDealDashboard, dateRange, use_accounting_date]);

  return (
    <Stack sx={{ p: "16px", borderRadius: "8px", bgcolor: "white", gap: "8px", boxShadow: "0 1px 2px 0 #1212171a" }}>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <Typography sx={{ color: "#071F35", fontSize: "18px", fontWeight: 500 }}>Deal Overview</Typography>
        <SelectDateRange
          formControlProps={{ sx: { minWidth: "200px" } }}
          label=""
          value={dateRange}
          onChange={setDateRange}
        />
      </Stack>
      <Stack direction="row" sx={{ gap: "10px" }}>
        {getCubicsLoading ? (
          <Skeleton variant="rounded" width="100%" height={60} />
        ) : (
          cubicData &&
          Object.entries(cubicData).map(([key, item]: [string, CubicData]) => (
            <Stack
              key={key}
              sx={{
                p: "8px 10px",
                border: "1px solid #DBE8F5",
                flexGrow: 1,
                justifyContent: "space-between",
                position: "relative",
                ".MuiIconButton-root": {
                  opacity: 0
                },
                ":hover": {
                  ".MuiIconButton-root": {
                    opacity: 1
                  }
                }
              }}
            >
              {Boolean(item.pme_warning) && !getCubicsLoading && (
                <MaterialTooltip isShow title={item.pme_warning} centerText placement="top">
                  <InfoOutlinedIcon
                    fontSize="sm"
                    sx={{ fill: "#071F35", position: "absolute", top: "22px", left: "10px" }}
                  />
                </MaterialTooltip>
              )}
              <Typography sx={{ textAlign: "center", fontSize: "15px", color: "#496682", fontWeight: 400 }}>
                {item.title}
              </Typography>
              <Typography sx={{ textAlign: "center", fontSize: "15px", color: "#071F35", fontWeight: 700 }}>
                {formatCubicValue(item, formatNumeric)}
              </Typography>
              <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                <IconButton
                  onClick={(e) =>
                    setMenuState({
                      cubic: item,
                      pageType: "cubicDealDashboard",
                      anchorEl: e.currentTarget,
                      itemKey: key,
                      onSubmit: handleApply
                    })
                  }
                  sx={{
                    position: "absolute",
                    right: "2px",
                    top: "2px",
                    zIndex: 100,
                    transition: "opacity .15s linear"
                  }}
                >
                  <TuneIcon sx={{ height: 20, width: 20, color: "#5d7782" }} />
                </IconButton>
              </RoleComponentGuard>
            </Stack>
          ))
        )}
      </Stack>
      {menuState && (
        <Popover
          onClose={() => setMenuState(null)}
          open={Boolean(menuState)}
          anchorEl={menuState?.anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left"
          }}
        >
          <Menu {...menuState} />
        </Popover>
      )}
    </Stack>
  );
};

interface MenuProps {
  cubic: CubicData;
  pageType: CubicPageType;
  onSubmit: (data: { filter: string; title: string; key: string; indexName?: { index: string; name: string } }) => void;
  anchorEl: HTMLElement;
  itemKey: string;
}

const Menu: React.FC<MenuProps> = ({ cubic, onSubmit, pageType, itemKey }) => {
  const [value, setValue] = useState<string>(cubic.filter);
  const [title, setTitle] = useState<string>(cubic.title);
  const [PMEIndex, setPMEIndex] = useState<{ index: string; name: string } | undefined>(
    cubic.indexName || { index: DEAL_INDEX, name: "Use deal specific index" }
  );

  useEffect(() => {
    if (value === cubic.filter) return;
    setValue(cubic.filter);
  }, [cubic]);

  useEffect(() => {
    if (title === cubic.title) return;
    setValue(cubic.title);
  }, [cubic]);

  const { KPIFields, KPICharacteristics } = useReportsMetaSelector();

  const kpiOptionsForSelect: string[] = useMemo(() => {
    return [...KPIFields, ...(pageType === "cubicDealDashboard" ? KPICharacteristics : [])];
  }, [KPIFields, KPICharacteristics, pageType]);

  const onHandleSubmit = () => {
    onSubmit({
      filter: value,
      title,
      key: itemKey,
      ...(value === "PME" && PMEIndex?.index !== DEAL_INDEX && { indexName: PMEIndex })
    });
  };

  return (
    <Stack sx={{ bgcolor: "white", p: "15px 10px", boxShadow: "sm", borderRadius: 5, gap: "15px", maxWidth: 350 }}>
      <SelectAutocomplete<string>
        options={kpiOptionsForSelect}
        value={value}
        setValue={(value) => {
          if (!value) return;
          setValue(value);
          if (title !== value) {
            setTitle(value);
          }
        }}
        renderInput={(params) => <TextField {...params} label="Value" size="small" />}
        limitTags={2}
      />
      {value === "PME" && (
        <SelectAutocomplete<string>
          options={["Use deal specific index", ...Object.values(indexNames)]}
          value={PMEIndex?.name}
          setValue={(selectedValue) => {
            if (!selectedValue) return;
            if (selectedValue === "Use deal specific index") {
              setPMEIndex({ index: DEAL_INDEX, name: selectedValue });
              return;
            }
            const indexKey = Object.entries(indexNames).find(([, val]) => val === selectedValue)?.[0];
            if (indexKey) {
              setPMEIndex({ index: indexKey, name: selectedValue });
            }
          }}
          renderInput={(params) => <TextField {...params} label="Index" size="small" />}
          limitTags={2}
        />
      )}
      <TextField
        value={title}
        label="Rename"
        variant="outlined"
        onChange={(event) => {
          setTitle(event.target.value);
        }}
      />
      <Button variant="contained" onClick={onHandleSubmit}>
        Apply
      </Button>
    </Stack>
  );
};
